import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import * as Styled from './ScoreOverviewModalPopulator.styled';
import UnitCompleteNext from '../UnitCompleteNext/UnitCompleteNext';
import { useSelector, useDispatch } from 'react-redux';
import {
  assessmentIdSelector,
  ChatStates,
  scoreOverviewVisibleSelector,
  selectChatState,
  selectDiagnosisSuccess,
  selectTimeSpent,
  selectTotalObjectivePointsAchieved,
  selectTotalPointsPossible,
  sessionReportSelector,
  setScoreOverviewVisible,
  setSessionReport,
} from '../../slices/chat/ChatSlice';
import {
  courseIDSelector,
  easyModeSelector,
  nextUnitSelector,
  shareTokenSelector,
  virtualHumanDataSelector,
} from '../../slices/VirtualHumanSlice';
import IconLabelButton from '../molecules/IconLabelButton/IconLabelButton';
import FinishSessionButton from '../FinishSessionButton/FinishSessionButton';
import ScoreOverview from '@core/components/Analytics/VirtualHuman/ScoreOverview/ScoreOverview';
import {
  languageSelector,
  objectiveGroupsSelector,
  objectivesSelector,
  patientSelector,
} from '@vh/slices/PatientSlice';
import { shownHintObjectiveIdsSelector } from '@vh/slices/ObjectivesSlice';
import { ScoreOverviewModal } from '@core/components/Analytics/VirtualHuman/ScoreOverviewModal/ScoreOverviewModal.styled';
import useUser from '@core/helpers/useUser';
import { useVHWebModalContext } from '@vh/components/VHWebModalBase/VHWebModalBaseContext';
import { useFakeProgressValue } from '@core/components/Loaders/ProgressLoader/useFakeProgressValue';
import { useGenerateSessionReport } from '@core/components/Analytics/VirtualHuman/ScoreOverview/useGenerateSessionReport';
import { useGetSessionReportUntilDone } from '@core/components/Analytics/VirtualHuman/ScoreOverview/useGetSessionReportUntilDone';
import useGetVHData from '@base/utility/VHHooks/useGetVHData';
import { CanSeeRegenerateCoachingReportButton } from '@base/utility/Permissions';
import { analyticsSubmissionIDSelector } from '@vh/slices/SilverstripeAPISlice';
import { showableScoreOverviewSelector } from '@vh/slices/vh-player/VHPlayerSliceExtraSelectors';
import { useTranslation } from 'react-i18next';
import { Button } from '@virtidev/toolbox';
import useLocalStorage from '@vh/utility/useLocalStorage';
import { isPublicUserSessionSelector } from '@vh/slices/vh-player/VHPlayerSlice';
import { isInCrossDomainIframe } from '@core/helpers/useIsIframe';

/**
 * @typedef {import('@core/models/vh-server/vh-server-coaching-session-report.types').CoachingSessionReport} CoachingSessionReport
 * @typedef {import('@core/models/vh-server/vh-server-coaching-session-report.types').CoachingSessionReportObjective} CoachingSessionReportObjective
 */

/** @type {FC<{}>} */
const ScoreOverviewModalPopulator = () => {
  // show score overview when it's set to show in redux
  const scoreOverviewVisible = useSelector(scoreOverviewVisibleSelector);
  const VHWebModalContext = useVHWebModalContext();
  const nextUnit = useSelector(nextUnitSelector);
  const courseID = useSelector(courseIDSelector);
  const chatState = useSelector(selectChatState);
  const dispatch = useDispatch();
  const handleShowScoreModal = useCallback(() => {
    dispatch(setScoreOverviewVisible(true));
  }, [dispatch]);

  const handleCloseScoreModal = useCallback(() => {
    dispatch(setScoreOverviewVisible(false));
  }, [dispatch]);

  const analyticsSubmissionID = useSelector(analyticsSubmissionIDSelector);
  const { ID: loggedInMemberID } = useUser();
  const isPublicUserSession = useSelector(isPublicUserSessionSelector);

  const [publicUserID] = useLocalStorage('public_user_id', '');
  let memberID = isPublicUserSession ? publicUserID : loggedInMemberID || '';
  const objectiveGroups = useSelector(objectiveGroupsSelector);
  const objectives = useSelector(objectivesSelector);
  const timeSpent = useSelector(selectTimeSpent);
  const diagnosisSuccess = useSelector(selectDiagnosisSuccess);
  const vhDBVH = useSelector(patientSelector);
  const hitObjectivesData = useSelector(
    (state) => state.chat?.finalScores?.hitObjectivesData
  );
  const vh = useSelector(virtualHumanDataSelector);
  const timeSuccess =
    timeSpent < vh?.TimeLimit * 60 * 1000 || vh?.TimeLimit === 0;
  const pointsScored = useSelector(selectTotalObjectivePointsAchieved);
  const pointsPossible = useSelector(selectTotalPointsPossible);
  const assessmentId = useSelector(assessmentIdSelector);
  const shownHintObjectiveIds = useSelector(shownHintObjectiveIdsSelector);
  const easyMode = useSelector(easyModeSelector);

  const { data } = useGetVHData(
    `virtual-humans/${vhDBVH.id}/coaching-session-report-generation-duration`
  );
  const estimatedGenerationDuration = data?.data ?? 40000;
  const { data: vhGeneratesReportsData } = useGetVHData(
    `virtual-humans/${vhDBVH.id}/generates-reports`
  );
  const vhGeneratesReports = vhGeneratesReportsData?.data?.data ?? false;

  const { fakeProgress, startProgressIncrementing, stopProgress } =
    useFakeProgressValue();

  const generatedReport = useSelector(sessionReportSelector);
  const language = useSelector(languageSelector);
  const { UserType } = useUser();
  const showRegenerateButton = CanSeeRegenerateCoachingReportButton();
  const shareToken = useSelector(shareTokenSelector);
  const [currentlyGeneratingReport, setCurrentlyGeneratingReport] =
    useState(false);

  const showableScoreOverview = useSelector(showableScoreOverviewSelector);
  // when session is complete start checking the status of the report
  const { data: sessionReportData, resetData } = useGetSessionReportUntilDone(
    chatState === ChatStates.COMPLETE && vhGeneratesReports,
    assessmentId,
    (completedReport) => {
      if (completedReport) {
        stopProgress();
        dispatch(setSessionReport(completedReport));
      }
    }
  );

  const memoizedArgs = useMemo(() => {
    return {
      assessmentId,
      onStarted: () => {
        console.log('generation started');
        setCurrentlyGeneratingReport(true);
        // setGenerationPreviouslyStarted(true);
        startProgressIncrementing(estimatedGenerationDuration, true);
        resetData();
        console.log('START INCREMENTING');
      },
      onComplete: (newReport) => {
        if (newReport) {
          dispatch(setSessionReport(newReport));
        }
        setCurrentlyGeneratingReport(false);
        stopProgress();
      },
      onError: (e) => {
        // setGenerationPreviouslyStarted(false);
        setCurrentlyGeneratingReport(false);
        stopProgress();
      },
    };
  }, [
    assessmentId,
    dispatch,
    resetData,
    startProgressIncrementing,
    estimatedGenerationDuration,
    stopProgress,
  ]);

  const { generateReport, generatingReport } =
    useGenerateSessionReport(memoizedArgs);

  const handleRegenerate = useCallback(() => {
    generateReport(assessmentId, true, shareToken);
  }, [assessmentId, generateReport, shareToken]);

  // if it's been autogenerated from the server we need to set off the fake progress
  useEffect(() => {
    if (generatedReport?.generatingStatus === 'pending') {
      startProgressIncrementing(estimatedGenerationDuration, false);
    }
  }, [generatedReport, startProgressIncrementing, estimatedGenerationDuration]);

  const handlePrintClick = useCallback(() => {
    localStorage.setItem(
      'print_vh_report_data',
      JSON.stringify({
        easyMode: vh.EasyMode,
        sessionReport: generatedReport,
        vhDBVH,
        ssVH: vh,
        timeSpent,
      })
    );
    window
      .open('/virtual-humans/coaching-session-report/print', '_blank')
      ?.focus();
  }, [generatedReport, timeSpent, vh, vhDBVH]);
  
  const { t } = useTranslation();

  const footerContent = useMemo(
    () => (
      <>
        {generatedReport && !isInCrossDomainIframe && (
          <Button
            color="turquoise"
            disabled={
              generatedReport?.generatingStatus !== 'complete' ||
              currentlyGeneratingReport
            }
            onClick={handlePrintClick}
            icon="expand"
          >
            {t('viewPrintable')}
          </Button>
        )}
        {nextUnit && (
          <UnitCompleteNext nextUnit={nextUnit} courseID={courseID} />
        )}
        {!nextUnit && <FinishSessionButton courseID={courseID} />}
      </>
    ),
    [
      generatedReport,
      currentlyGeneratingReport,
      handlePrintClick,
      t,
      nextUnit,
      courseID,
    ]
  );


  if (chatState !== ChatStates.COMPLETE) {
    return <></>;
  }

  return (
    <ScoreOverviewModal
      className="score-overview-modal"
      show={scoreOverviewVisible}
      onHide={handleCloseScoreModal}
      containerRef={VHWebModalContext.current}
      title={showableScoreOverview ? t('coachingReport') : t('sessionComplete')}
    >
      {showableScoreOverview ? (
        <ScoreOverview
          onRegenerate={handleRegenerate}
          currentlyGeneratingReport={currentlyGeneratingReport}
          sessionReport={generatedReport ?? undefined}
          generationProgress={fakeProgress}
          objectiveGroups={objectiveGroups}
          allObjectives={objectives}
          timeSpent={timeSpent}
          vhDBVH={vhDBVH}
          ssVH={vh}
          hitObjectivesData={hitObjectivesData}
          pointsScored={pointsScored}
          pointsPossible={pointsPossible}
          hideScores={vh.HideScoresFromUsers}
          shownHintObjectiveIds={shownHintObjectiveIds}
          easyMode={easyMode}
          showRegenerateButton={showRegenerateButton}
          footer={footerContent}
          allowLearnerFeedbackSetting={true}
          memberID={memberID + ''}
          learnerFeedbackRating={undefined}
          analyticsSubmissionId={analyticsSubmissionID}
          createdDate={null}
        />
      ) : (
        <Styled.SessionCompleteModalContent>
          <div>
            Congratulations! You've completed this Virtual Human session.
          </div>
          {footerContent}
        </Styled.SessionCompleteModalContent>
      )}
    </ScoreOverviewModal>
  );
};

export default ScoreOverviewModalPopulator;